import { useEffect, useRef, useState } from "react"
import api from "../../../../base/services/ResApi"
import "../../../../styleSheets/base/styles.scss"
import "../styles/terminals.styles.scss"
import { Tabs, Tab } from "react-bootstrap"
import { Dialog, DialogTitle, IconButton } from "@mui/material"
import CloseIcon from "@mui/icons-material/Close"
import GeneralInfo from "./terminalsMoreInfo/GeneralInfo"
import TerminalHealth from "./terminalsMoreInfo/TerminalHealth"
import VersionInfo from "./terminalsMoreInfo/VersionInfo"
import ConnectivityInfo from "./terminalsMoreInfo/ConnectivityInfo"
import Logs from "./terminalsMoreInfo/logs/Logs"
import TerminalHealthHistory from "./terminalsMoreInfo/TerminalHealthHistory"
import OperationHistory from "./terminalsMoreInfo/operations/OperationHistory"

const ApproveGrpMoreInfoModal = ({ open, onClose, approvedData: initialData }) => {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth)
  const [approvedData, setApprovedData] = useState(initialData)
  const [refreshing, setRefreshing] = useState(false)
  const [refreshError, setRefreshError] = useState("")
  const refreshRequest = useRef(0)
  const refreshPending = useRef(false)

  useEffect(() => {
    setApprovedData(initialData)
    setRefreshing(false)
    setRefreshError("")
    refreshPending.current = false
    return () => { refreshRequest.current += 1 }
  }, [initialData, open])

  const refreshTerminal = async () => {
    if (!open || !initialData?.serialNo || refreshPending.current) return
    const requestId = ++refreshRequest.current
    refreshPending.current = true
    setRefreshing(true)
    setRefreshError("")
    try {
      const response = await api.get("/terminals", {
        headers: {
          Action: "V_ALL_APPR_TMNL",
          "Sort-Column": "tmnl_id",
          "Sort-Order": "asc",
          Filter: "F_TRMNL_SERIAL",
          Value: initialData.serialNo,
          "Is-Pagination": "false",
          "X-Additional-Info": true,
        },
      })
      if (requestId !== refreshRequest.current) return
      const terminal = response.data?.dataList?.find((item) => item.serialNo === initialData.serialNo)
      if (!terminal) throw new Error("Terminal not found")
      // Match the table-row shape used when initially opening this dialog.
      setApprovedData({ ...terminal, connStatus: terminal.sseConnectionDtl?.connStatus ?? null })
    } catch (error) {
      if (requestId === refreshRequest.current) setRefreshError("Unable to refresh terminal details. Please try again.")
    } finally {
      if (requestId === refreshRequest.current) {
        refreshPending.current = false
        setRefreshing(false)
      }
    }
  }

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth)
    }
    window.addEventListener("resize", handleResize)
    return () => {
      window.removeEventListener("resize", handleResize)
    }
  }, [])

  return (
    <Dialog
      fullWidth={true}
      maxWidth="true"
      open={open}
      PaperProps={{
        style: {
          borderRadius: "10px",
          width: windowWidth >= 1500 ? "1500px" : "1320px",
          margin: 0,
        },
      }}
    >
      <DialogTitle className="terminalInfoTitle">
        Terminal Info - {approvedData?.serialNo}
        <IconButton
          aria-label="cancel"
          sx={{ color: "#fff" }}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Tabs
        style={{ marginTop: 80, zIndex: 100 }}
        defaultActiveKey="GeneralInfo"
        id="uncontrolled-tab-example"
        className="mb-3"
        fill
      >
        <Tab eventKey="GeneralInfo" title={"General"}>
          <GeneralInfo
            onRefresh={refreshTerminal}
            refreshing={refreshing}
            refreshError={refreshError}
            open={open}
            onClose={onClose}
            approvedData={approvedData}
          />
        </Tab>
        <Tab eventKey="OperHistory" title={"Operation History"}>
          <OperationHistory
            open={open}
            onClose={onClose}
            approvedData={approvedData}
            serialNo={approvedData?.serialNo}
          />
        </Tab>
        <Tab eventKey="TerminalHealth" title={"Health"}>
          <TerminalHealth
            open={open}
            onClose={onClose}
            approvedData={approvedData}
            serialNo={approvedData?.serialNo}
          />
        </Tab>
        <Tab eventKey="TerminalHealthHistory" title={"Health History"}>
          <TerminalHealthHistory
            open={open}
            onClose={onClose}
            approvedData={approvedData}
            serialNo={approvedData?.serialNo}
          />
        </Tab>
        <Tab eventKey="Logs" title={"Logs"}>
          <Logs
            open={open}
            onClose={onClose}
            approvedData={approvedData}
            serialNo={approvedData?.serialNo}
          />
        </Tab>
        <Tab eventKey="VersionInfo" title={"Version History"}>
          <VersionInfo
            open={open}
            onClose={onClose}
            approvedOrgData={approvedData}
            serialNo={approvedData?.serialNo}
          />
        </Tab>
        <Tab disabled eventKey="Connectivity" title={"Connectivity History"}>
          <ConnectivityInfo open={open} onClose={onClose} />
        </Tab>
      </Tabs>
    </Dialog>
  )
}

export default ApproveGrpMoreInfoModal
